import React, {Suspense, Fragment} from 'react';
import {render} from 'react-dom';
import store from 'store';
import qs from 'qs';

import {
	Router,
	Route,
	Switch,
} from 'react-router-dom';

import {Relay} from './relay'

import {
	Card,
	AddLink,
	LoadingSpinner,
	ErrorBoundary,
	Button,
	Modal,
	useRelayErrors,
	ErrorStripe,
	FormGroup,
	InputComponent,
	DeleteModalComponent,
	FunnelsListPickerApp,
	Upgrade,
} from './components';
import {Connect} from "./components/connect";

import {
	useLocalStore,
	Loaders,
	setupLoaders,
} from './data';

import {createBrowserHistory} from 'history';

import {
	useLazyLoadQuery,
	graphql,
	useRelayEnvironment,
	fetchQuery,
	useMutation,
	loadQuery,
	usePreloadedQuery,
} from 'react-relay';

import type {tsShopConnectMutation, tsShopConnectMutation$data} from './__generated__/tsShopConnectMutation.graphql';
import type {tsShopUpgradeMutation, tsShopUpgradeMutation$data} from './__generated__/tsShopUpgradeMutation.graphql';
import type {tsDeleteMutation} from './__generated__/tsDeleteMutation.graphql';
import type {tsAppQuery, tsAppQuery$data} from './__generated__/tsAppQuery.graphql';
import type {tsCreateMutation} from './__generated__/tsCreateMutation.graphql';

import '../styles/style.scss';

const history = createBrowserHistory();

render(
	<div className="appContainer">
		<Router history={history}>
			<Switch>
				<Route path="/app" component={Middleware} />
				<Route component={Home} />
			</Switch>
		</Router>
	</div>,
	window.app
);

function Provider(props: {children: React.ReactNode}) {
	const env = useRelayEnvironment();

	const loaders = React.useMemo(
		function () {
			return setupLoaders(env);
		},
		[env]
	);

	return (
		<Loaders.Provider value={loaders}>
			{props.children}
		</Loaders.Provider>
	)
}

function Middleware(props){
	
	const q = React.useMemo(() => {
		return qs.parse(props.location.search, {ignoreQueryPrefix: true});
	}, [props.location.search]);

	const [token, setToken] = React.useState<undefined|string>(() => {
		return q['installation-id'] && store.get(installToKey(q['installation-id']))
	});

	React.useEffect(
		function(){
			if(q.code && q.state && q['account-id']){
				fetch(process.env.ApiURL + 'lightfunnels/connect-response', {
					method:'POST',
					headers:{
						'Content-Type': 'application/json'
					},
					body: JSON.stringify({
						code: q.code,
						state: q.state,
						accountID: q['account-id']
					})
				})
				.then(resp => resp.json())
				.then(function(resp){
					store.set(installToKey(resp.id), resp.token);
					setToken(resp.token);
				});
			} else if(!token && q['account-id']){
				fetch(process.env.ApiURL + 'lightfunnels/connect-url', {
					method:'POST',
					headers:{
						'Content-Type': 'application/json'
					},
					body: JSON.stringify({
						accountID: q['account-id'],
						redirectUrl: window.location.origin + '/app'
					})
				})
				.then(resp => resp.text())
				.then(function(resp){
					// console.log('1-',resp);
					window.location.href = resp;
				});
			}
		},[]
	);

	if(!token){
		return (
			<Fragment children={<LoadingSpinner />} />
		);
	}
	
	return (
		<Suspense fallback={<LoadingSpinner />}>
			<Relay token={token}>
				<Provider>
					<ErrorBoundary>
						<App/>
					</ErrorBoundary>
				</Provider>
			</Relay>
		</Suspense>
	);
}

function App(){
	
	// fetch infos
	const resp = useLazyLoadQuery<tsAppQuery>(
		graphql`
			query tsAppQuery($offset: Int){
				orders(offset: $offset){
					id
					lightfunnels_order_id
					connection_id
					shopify_order_id
					created_at
					connection{
						shop{
							id
							shop
						}
					}
				}
				connections{
					id
					_id
					shop{
						id
						shop
					}
					funnel{
						id
						name
					}
				}
				account{
					id
					shops {
						_id
						id
						shop
						v2
						scopes
					}
				}
			}
		`,
		{}
	);

	const account = resp.account;
	const connections = resp.connections;
	const orders = resp.orders;

	const isConnected = account.shops.length>0;

	const [isOpen, openForm] = React.useState<boolean>(false);
	const [del, setDel] = React.useState<number|null>(null);
	const [onError, errors, setErrors] = useRelayErrors();
	const [deleteCon, deleteConLoading] = useMutation<tsDeleteMutation>(
		graphql`
			mutation tsDeleteMutation($id: Int!){
				deleteConnection(id: $id)
			}
		`
	);

	const [connectStoreState, setConnectStoreState] = React.useState<boolean>(false);
	const [connectStore, connectStoreLoading] = useMutation<tsShopConnectMutation>(
		graphql`
			mutation tsShopConnectMutation($shop: String!, $token: String!, $secret: String!) {
				connectStore2(shop: $shop, token: $token, secret: $secret){
					shops {
						_id
						id
						shop
						v2
					}
				}
			}
		`
	);

	const [upgradeShop, setUpgradeShop] = React.useState<number|null>(null);
	const [upgradeStore, upgradeShopLoading] = useMutation<tsShopUpgradeMutation>(
		graphql`
			mutation tsShopUpgradeMutation($id: Int!, $token: String!, $secret: String!) {
				upgradeStore(id: $id, token: $token, secret: $secret){
					id
					v2
				}
			}
		`
	);
	
	return (
		<div className="app">
			{
				del && (
					<DeleteModalComponent
						children="Are you sure you want to delete this connection? It can't be undone."
						title="Delete Connection"
						error={errors[""]}
						onCancel={() => setDel(null)}
						loading={deleteConLoading}
						onConfirm={
							function () {
								if(deleteConLoading){
									return;
								}
								setErrors([]);
								deleteCon({
									variables:{
										id: del
									},
									onError,
									updater(store){
										const root = store.getRoot();
										const newCons = root.getLinkedRecords('connections')!.filter(w => w.getValue('_id') !== del);
										root.setLinkedRecords(newCons, "connections");
									},
									onCompleted(data){
										if(data.deleteConnection){
											setDel(null);
										}
									}
								});
							}
						}
					/>
				)
			}
			{
				connectStoreState && (
					<Connect
						close={() => {
							setConnectStoreState(false);
						}}
						loading={connectStoreLoading}
						action={(variables: {token: string, secret: string, shop: string}, onError) => {
							connectStore({
								variables,
								onError,
								onCompleted(){
									setConnectStoreState(false);
								}
							});
						}}
					/>
				)
			}
			{
				Boolean(upgradeShop) && (
					<Upgrade
						close={() => {
							setUpgradeShop(null);
						}}
						loading={upgradeShopLoading}
						action={(state: {token: string, secret: string}, onError) => {
							upgradeStore({
								variables:{
									token: state.token,
									secret: state.secret,
									id: upgradeShop!,
								},
								onError,
								onCompleted(){
									setUpgradeShop(null);
								}
							});
						}}
					/>
				)
			}

			<Button
				className="connectButton"
				children="Connect Shopify Store"
				primary
				spinning={connectStoreLoading}
				onClick={function (){
					setConnectStoreState(true);
				}}
			/>

			{/* shops */}
			<Card
				title="Connected Stores"
				body={
					account.shops.length>0 ? (
						<table className="table card-table table-vcenter">
							<thead>
								<tr>
									<th>ID</th>
									<th>Store</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{
									account.shops.map(
										function(shop) {
											return(
												<tr key={shop._id}>
													<td>{shop._id}</td>
													<td>{shop.shop}</td>
													<td>
														{
															shop.v2 ?
																null :
																<span>
																	<span
																		className="cursor-pointer text-red"
																		children={
																			<span>
																				<i className="ti ti-alert-circle me-1" />
																				Deprecated (click to upgrade)
																			</span>
																		}
																		onClick={() => {
																			setUpgradeShop(shop._id)
																		}}
																	/>
																</span>
														}
													</td>
												</tr>
											)
										}
									)
								}
							</tbody>
						</table>
						) : (
							<div className="card-body">
								There are no shops connected yet.
							</div>
						)
				}
			/>
			
			

			{/* automations */}
			<Card
				title="Automations"
				actions={
					isConnected ? <AddLink children="Add Connection" onClick={() => openForm(true)} /> : null
				}
				body={
					(connections.length>0) ? (
						<table className="table card-table table-vcenter">
							<thead>
								<tr>
									<th>ID</th>
									<th>Store</th>
									<th>Funnel</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{
									connections.map(
										function(con) {
											return(
												<tr key={con._id}>
													<td>{con._id}</td>
													<td>{con.shop.shop}</td>
													<td>
														{con.funnel?.name ?? <span className="text-muted">All Funnels</span>}
													</td>
													<td className="text-end">
														<Button
															danger
															children={<i className="ti ti-trash"></i>}
															onClick={
																() => {
																	setDel(con._id);
																}
															}
														/>
													</td>
												</tr>
											)
										}
									)
								}
							</tbody>
						</table>
					) : (
						<div className="card-body">
							There are no connections yet.
						</div>
					)
				}
			/>

			{/* records */}
			<Card
				title="Records"
				body={
					(orders.length>0) ? (
						<table className="table card-table table-vcenter">
							<thead>
								<tr>
									<th>Order ID</th>
									<th>Shopify Order ID</th>
								</tr>
							</thead>
							<tbody>
								{
									orders.map(
										function(rec) {
											return(
												<tr key={rec.lightfunnels_order_id}>
													<td>
														<a href={`https://app.lightfunnels.com/admin/orders/${rec.lightfunnels_order_id}`} target="_blank" children={rec.lightfunnels_order_id} />
													</td>
													<td>
														<a href={`https://${rec.connection.shop.shop}/admin/orders/${rec.shopify_order_id}`} target="_blank" children={rec.shopify_order_id} />
													</td>
												</tr>
											)
										}
									)
								}
							</tbody>
						</table>
					) : (
						<div className="card-body">
							There are no orders connected yet.
						</div>
					)
				}
			>
			</Card>

			{
				isOpen && (
					<ConnectionForm closeForm={() => openForm(false)} shops={account.shops as tsAppQuery$data['account']['shops']}/>
				)
			}
		</div>
	)
}

function ConnectionForm(props: {closeForm: () => void; shops: tsAppQuery$data['account']['shops'];}){
	const [onError, errors, setErrors] = useRelayErrors();
	const [create, loading] = useMutation<tsCreateMutation>(
		graphql`
			mutation tsCreateMutation($node: ConnectionInput!){
				createConnection(node: $node) {
					_id
					id
					shop{
						id
						shop
					}
					funnel{
						id
						name
					}
				}
			}
		`
	);

	const [state, onChange] = useLocalStore<{shop: number|null; funnel: number|null}>({
		shop: null,
		funnel: null
	});

	return(
		<Modal
			close={props.closeForm}
			title="Add Connection"
			footer={
				<Fragment>
					<Button
						disabled={!state.shop}
						children="Create Connection"
						spinning={loading}
						primary
						onClick={
							function() {
								create({
									variables: {
										node: {
											funnel_id: state.funnel,
											shop_id: state.shop as number
										}
									},
									onError,
									updater(store, data) {
										const root = store.getRoot();
										root.setLinkedRecords(
											root.getLinkedRecords('connections')!.concat(store.getRootField('createConnection')),
											"connections"
										);
									},
									onCompleted(){
										props.closeForm();
									}
								})
							}
						}
					/>
				</Fragment>
			}
		>
			{
				errors[''] && <ErrorStripe message={errors['']} />
			}
			{
				errors.shop_id && <ErrorStripe message={errors.shop_id} />
			}
			<div>
				<FormGroup label="Funnel">
					<FunnelsListPickerApp
						cancellable
						error={errors.funnel_id}
						fragment={funnelsPaginationFragment}
						query={funnelsQuery}
						value={state.funnel}
						label="All Funnels"
						onChange={
							function(funnel_id){
								onChange(['funnel'], funnel_id);
							}
						}
					/>
				</FormGroup>
				<FormGroup label="Store">
					<select
						value={state.shop ?? ""}
						className="form-select"
						onChange={event => {
							onChange(['shop'], Number(event.target.value));
						}}
					>
						{
							!state.shop && <option value="" disabled />
						}
						{
							props.shops.map(shop => {
								return (
									<option key={shop.id} value={shop._id} children={shop.shop} />
								)
							})
						}
					</select>
				</FormGroup>
			</div>
		</Modal>
	)
}

function Home(){
	return (
		<div className="empty">
			<p className="empty-title">Welcome to the Lightfunnels' Shopify application.</p>
			<div className="empty-action">
				<a target="_blank" href="https://app.lightfunnels.com/admin/apps" className="btn btn-primary">
					Install app
				</a>
			</div>
		</div>
	);
}


function installToKey(id){
	return 'i' + id + '_token';
}

const funnelsQuery = graphql`
	query tsFunnelsQuery($first: Int, $after: String, $query: String!){
		...tsFunnelsPagination
	}
`;

const funnelsPaginationFragment = graphql`
	fragment tsFunnelsPagination on Query
	@refetchable(queryName: "tsFunnelsPaginationQuery") {
		pagination: funnels(first: $first, after: $after, query: $query)
		@connection(key: "funnels_pagination", filters:["query"]){
			edges {
				node{
					value: _id
					label: name
				}
			}
			pageInfo{
				endCursor
				hasNextPage
			}
		}
	}
`;
